import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Vertical } from '../../../interfaces/users';

export enum TeamAccountType {
  STANDARD = 'STANDARD',
  LEGAL = 'LEGAL',
  STANDARD_BOT_ENABLED = 'STANDARD_BOT_ENABLED'
}

export interface UseTeamAccountTypeReturn {
  accountType: TeamAccountType;
  isLegal: boolean;
  isStandard: boolean;
  isStandardBotEnabled: boolean;
  teamName: string;
}

/**
 * Hook to determine team account type based on team attributes
 * Returns STANDARD, LEGAL, or STANDARD_BOT_ENABLED
 */
export const useTeamAccountType = (): UseTeamAccountTypeReturn => {
  const team = useSelector((state: RootState) => state.team.value);
  const accountType = useMemo((): TeamAccountType => {
    if (!team) {
      return TeamAccountType.STANDARD;
    }

    // Check if this is a legal team based on vertical
    if (team.vertical === Vertical.LEGAL_INTAKE) {
      return TeamAccountType.LEGAL;
    }

    // Check if this is a standard team with bot access enabled
    if (team.has_practice_bot_access) {
      return TeamAccountType.STANDARD_BOT_ENABLED;
    }

    // Default to standard
    return TeamAccountType.STANDARD;
  }, [team]);

  const isLegal = accountType === TeamAccountType.LEGAL || window.location.hostname.includes('platform.capturenow');
  const isStandard = accountType === TeamAccountType.STANDARD;
  const isStandardBotEnabled = accountType === TeamAccountType.STANDARD_BOT_ENABLED;
  
  // Get team name, fallback to "Team" if not available
  const teamName = team?.team || "Team";

  return {
    accountType,
    isLegal,
    isStandard,
    isStandardBotEnabled,
    teamName
  };
};